import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";

export default function Privacy() {
  const sections = usePrivacySections();
  const { t } = useTranslation();

  return (
    <>
        <section className="bg-background">
          <div className="container py-16 md:py-20">
            <div className="mx-auto max-w-3xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-4 py-1.5 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4" />
                {t("privacyPage.badge")}
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                {t("privacyPage.title")}
              </h1>
              <p className="mt-4 text-muted-foreground">
                {t("privacyPage.lastUpdated")}
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted">
          <div className="container">
            <div className="mx-auto max-w-3xl space-y-10">
              {sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.content.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-muted-foreground leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
    </>
  );
}

/**
 * Read this locale's privacy policy sections out of the locale bundle.
 *
 * Split out of the component only so the untyped ``returnObjects`` boundary
 * (``SYSTEM_DESIGN.md`` §3.1) has one named home rather than sitting inline in
 * the JSX. The ``as`` is an assertion, not a check: a locale edit that renames
 * ``privacyPage.sections`` makes ``t()`` return the key string, ``.map()``
 * throw, and — since CWS-5 — degrade to the contained error fallback instead
 * of the whole-page blank this comment originally guarded against.
 * ``Privacy.test.tsx`` mounts this in all five locales for exactly that reason.
 *
 * @returns The sections as authored in the active locale.
 */
function usePrivacySections() {
  const { t } = useTranslation();

  return t("privacyPage.sections", { returnObjects: true }) as Array<{
    title: string;
    content: string[];
  }>;
}
